import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["enableFullVirtualization"];
import { memo } from 'react';
import { useVirtualizer } from '@tanstack/react-virtual';
import Row from './Row';
import { TABLE_ROW_HEIGHT } from '../constants/sizes';
import { useScrollWindowRef } from '../context/ScrollWindowRefContext';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
// Consumers with typical pagination will have very few rows outside of the 25 row buffer.
// Consumers with infinite scroll can have many rows, so a medium sized buffer
// helps to smooth the virtualization experience without too much overhead.
const OVERSCAN_AMOUNT = 10;
const NonVirtualizedTableBody = ({
  RowComponent,
  getRowId,
  rows,
  columns,
  selectable,
  numberOfFrozenColumns = 0
}) => {
  return /*#__PURE__*/_jsx("tbody", {
    children: rows.map((row, index) => {
      const rowId = getRowId(row);
      return /*#__PURE__*/_jsx(Row, {
        RowComponent: RowComponent,
        rowId: rowId,
        rowIndex: index,
        row: row,
        columns: columns,
        selectable: selectable,
        numberOfFrozenColumns: numberOfFrozenColumns
      }, rowId);
    })
  });
};
const VirtualizedTableBody = ({
  RowComponent,
  getRowId,
  rows,
  columns,
  selectable,
  numberOfFrozenColumns = 0
}) => {
  var _firstVirtualRow$star, _lastVirtualRow$end;
  const {
    element: scrollElement
  } = useScrollWindowRef();
  const virtualizer = useVirtualizer({
    count: rows.length,
    getScrollElement: () => scrollElement,
    estimateSize: () => TABLE_ROW_HEIGHT,
    overscan: OVERSCAN_AMOUNT
  });
  const virtualRows = virtualizer.getVirtualItems();
  const firstVirtualRow = virtualRows[0];
  const lastVirtualRow = virtualRows[virtualRows.length - 1];
  const topSpacerHeight = (_firstVirtualRow$star = firstVirtualRow === null || firstVirtualRow === void 0 ? void 0 : firstVirtualRow.start) !== null && _firstVirtualRow$star !== void 0 ? _firstVirtualRow$star : 0;
  const bottomSpacerHeight = virtualizer.getTotalSize() - ((_lastVirtualRow$end = lastVirtualRow === null || lastVirtualRow === void 0 ? void 0 : lastVirtualRow.end) !== null && _lastVirtualRow$end !== void 0 ? _lastVirtualRow$end : 0);
  return /*#__PURE__*/_jsxs("tbody", {
    children: [topSpacerHeight > 0 && /*#__PURE__*/_jsx("tr", {
      "aria-hidden": "true",
      style: {
        height: topSpacerHeight,
        padding: 0,
        border: 'none'
      }
    }), virtualRows.map(virtualRow => {
      const row = rows[virtualRow.index];
      const rowId = getRowId(row);
      return /*#__PURE__*/_jsx(Row, {
        RowComponent: RowComponent,
        rowId: rowId,
        rowIndex: virtualRow.index,
        row: row,
        columns: columns,
        selectable: selectable,
        numberOfFrozenColumns: numberOfFrozenColumns,
        measureElement: virtualizer.measureElement,
        virtualIndex: virtualRow.index
      }, rowId);
    }), bottomSpacerHeight > 0 && /*#__PURE__*/_jsx("tr", {
      "aria-hidden": "true",
      style: {
        height: bottomSpacerHeight,
        padding: 0,
        border: 'none'
      }
    })]
  });
};
const TableBody = _ref => {
  let {
      enableFullVirtualization = false
    } = _ref,
    props = _objectWithoutPropertiesLoose(_ref, _excluded);
  if (enableFullVirtualization) {
    return /*#__PURE__*/_jsx(VirtualizedTableBody, Object.assign({}, props));
  }
  return /*#__PURE__*/_jsx(NonVirtualizedTableBody, Object.assign({}, props));
};
export default /*#__PURE__*/memo(TableBody);